// Channel activity (#48, #58, #59): a flame after a channel's name in the list for the busiest channels since you last
// read them, instead of the unread dot. A dot means "new messages"; a flame means "a lot of them". One set of rules so
// every client marks the same channels.

/** How far back `Channel.weekMessages` counts. */
export const ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** The share of a server's text channels that can have a flame at once (at least one). */
export const FLAME_SHARE = 0.1;

/** The fewest new messages for a flame: below it, the busiest channel is still only unread. */
export const FLAME_MIN = 10;

/**
 * New messages in a channel since my last read, at most the past week's (#59): `weekMessages` was counted when the
 * channel list came (`weekSeq` is the channel's `lastSeq` then), so messages since then add to that ceiling.
 */
export function newSinceRead(c: { lastSeq: number; weekMessages?: number; weekSeq?: number }, read: number): number {
  const unread = Math.max(0, c.lastSeq - read);
  if (c.weekMessages === undefined) return unread;
  return Math.min(unread, c.weekMessages + Math.max(0, c.lastSeq - (c.weekSeq ?? c.lastSeq)));
}

/**
 * The channels with a flame: the top `FLAME_SHARE` of the server's `total` text channels (at least one) by new
 * messages, among those with at least `FLAME_MIN`. Ties go to the earlier channel in `counts` (the sidebar's order).
 */
export function flameChannels(counts: { id: string; count: number }[], total: number): Set<string> {
  const room = Math.max(1, Math.floor(total * FLAME_SHARE));
  const busiest = counts
    .map((c, i) => ({ ...c, i }))
    .filter((c) => c.count >= FLAME_MIN)
    .sort((a, b) => b.count - a.count || a.i - b.i);
  return new Set(busiest.slice(0, room).map((c) => c.id));
}
